import { Fill, Stroke, Style } from 'ol/style';
import { ParcelStatus } from '../models/parcel-status.model';
import { ParcelSource } from '../models/parcel-source.model';
import { type ParcelItem } from '../models/parcel-item.model';
import { ProplantaStatus } from '../../proplanta';

const maxParcelsAreaTolerancePercent = 1.05;

const minParcelsAreaTolerancePercent = 0.95;

export const RED_POLYGON_STYLE = new Style({
  fill: new Fill({ color: 'rgba(255, 0, 0, 0.5)' }),
  stroke: new Stroke({ color: 'red' })
});
export const YELLOW_POLYGON_STYLE = new Style({
  fill: new Fill({ color: 'rgba(255, 165, 0, 0.5)' }),
  stroke: new Stroke({ color: 'orange' })
});
export const GREEN_POLYGON_STYLE = new Style({
  fill: new Fill({ color: 'rgba(0, 140, 0, 0.25)' }),
  stroke: new Stroke({ color: 'green' })
});

const customerParcelStateColorMap: Record<ParcelStatus, string> = {
  [ParcelStatus.EDITING]: 'blue',
  [ParcelStatus.FINALIZED]: 'green',
  [ParcelStatus.NEED_YEARLY_DATA]: 'blue',
  [ParcelStatus.WAITING_FOR_FINALIZATION]: 'blue',
  [ParcelStatus.AWAITING_YIELD_DATA]: 'orange',
  [ParcelStatus.AWAITING_HISTORICAL_YIELD_DATA]: 'orange',
  [ParcelStatus.AWAITING_PLANNED_CROP_DATA]: 'blue',
  [ParcelStatus.AGRO_MAP_COMPLETED]: 'orange',
  [ParcelStatus.AGRO_MAP_ORDERED]: 'blue'
};

const adminParcelStateColorMap: Record<ParcelStatus, string> = {
  [ParcelStatus.EDITING]: 'blue',
  [ParcelStatus.FINALIZED]: 'green',
  [ParcelStatus.NEED_YEARLY_DATA]: 'blue',
  [ParcelStatus.WAITING_FOR_FINALIZATION]: 'blue',
  [ParcelStatus.AWAITING_YIELD_DATA]: 'blue',
  [ParcelStatus.AWAITING_HISTORICAL_YIELD_DATA]: 'blue',
  [ParcelStatus.AWAITING_PLANNED_CROP_DATA]: 'blue',
  [ParcelStatus.AGRO_MAP_COMPLETED]: 'orange',
  [ParcelStatus.AGRO_MAP_ORDERED]: 'blue'
};

const parcelStateTitleMap: Record<ParcelStatus, string> = {
  [ParcelStatus.EDITING]: 'parcel.status.editing',
  [ParcelStatus.FINALIZED]: 'parcel.status.finalized',
  [ParcelStatus.NEED_YEARLY_DATA]: 'parcel.status.NEED_YEARLY_DATA',
  [ParcelStatus.WAITING_FOR_FINALIZATION]: 'parcel.status.waiting_for_finalization',
  [ParcelStatus.AWAITING_YIELD_DATA]: 'parcel.status.awaiting_yield_data',
  [ParcelStatus.AWAITING_HISTORICAL_YIELD_DATA]: 'parcel.status.awaiting_historical_yield_data',
  [ParcelStatus.AWAITING_PLANNED_CROP_DATA]: 'parcel.status.awaiting_planned_crop_data',
  [ParcelStatus.AGRO_MAP_COMPLETED]: 'parcel.status.agro_map_completed',
  [ParcelStatus.AGRO_MAP_ORDERED]: 'parcel.status.agro_map_ordered'
};

const parcelSourceMap: Record<ParcelSource, string> = {
  [ParcelSource.AGRO_MAP]: 'parcel.source.agro_map',
  [ParcelSource.VANTAGE_DRAW]: 'parcel.source.draw',
  [ParcelSource.VANTAGE_FILE_UPLOAD]: 'parcel.source.file'
};

const proPlantaStatusColorMap: Record<ProplantaStatus, string> = {
  [ProplantaStatus.EDITING]: 'blue',
  [ProplantaStatus.FINALIZED]: 'green',
  [ProplantaStatus.NEED_YEARLY_DATA]: 'blue',
  [ProplantaStatus.WAITING_FOR_FINALIZATION]: 'blue',
  [ProplantaStatus.PARCEL_NOT_FINALIZED]: 'grey'
};

export function getParcelSourceText(source: ParcelSource): string {
  return parcelSourceMap[source];
}

export function needHighlightingBasedOnStatus(status: ParcelStatus): boolean {
  return status === ParcelStatus.NEED_YEARLY_DATA;
}

export function getAdminParcelStateColor(status: ParcelStatus): string {
  return adminParcelStateColorMap[status];
}

export function getCustomerParcelStateColor(status: ParcelStatus): string {
  return customerParcelStateColorMap[status];
}

export function getProPlantaStatusColor(status: ProplantaStatus): string {
  return proPlantaStatusColorMap[status];
}

export function getParcelStateTitle(status: ParcelStatus): string {
  return parcelStateTitleMap[status];
}

export function getProplantaStateTitle(status: ProplantaStatus): string {
  return parcelStateTitleMap[status];
}

export function needPercentBasedOnStatus(status: ParcelStatus): boolean {
  return status !== ParcelStatus.FINALIZED;
}

export function needPercentBasedOnProplantaStatus(status: ProplantaStatus): boolean {
  return status !== ProplantaStatus.FINALIZED;
}

export function parcelIsDeletable(parcelItem: ParcelItem): boolean {
  return (
    [ParcelStatus.EDITING, ParcelStatus.WAITING_FOR_FINALIZATION].includes(parcelItem.status) &&
    (!parcelItem.agroMapOrdered || parcelItem.source !== ParcelSource.AGRO_MAP)
  );
}

export function parcelIsMovable(parcelItem: ParcelItem): boolean {
  return (
    [ParcelStatus.EDITING, ParcelStatus.WAITING_FOR_FINALIZATION].includes(parcelItem.status) &&
    (!parcelItem.agroMapOrdered || parcelItem.source !== ParcelSource.AGRO_MAP)
  );
}

export function parcelIsFinalized(status: ParcelStatus): boolean {
  return [
    ParcelStatus.FINALIZED,
    ParcelStatus.AWAITING_YIELD_DATA,
    ParcelStatus.AWAITING_PLANNED_CROP_DATA,
    ParcelStatus.AWAITING_HISTORICAL_YIELD_DATA
  ].includes(status);
}

export function getParcelStateFilterOptions() {
  return [
    ParcelStatus.EDITING,
    ParcelStatus.FINALIZED,
    ParcelStatus.WAITING_FOR_FINALIZATION,
    ParcelStatus.NEED_YEARLY_DATA
  ].map((state) => ({
    label: parcelStateTitleMap[state],
    value: state
  }));
}

export function parcelsAreaTooLarge(finalizedArea: number, areaLimit: number): boolean {
  return finalizedArea / areaLimit > maxParcelsAreaTolerancePercent;
}

export function parcelsAreaTooSmall(uploadedArea: number, requiredArea: number): boolean {
  return uploadedArea / requiredArea < minParcelsAreaTolerancePercent;
}
